import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowsLeftRight, TrashSimple, CaretRight, CaretLeft, PencilSimple, User, Person, ArrowUUpLeft, ArrowsOut, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatItalianDate } from "@/lib/dateFormat";
import SmartDateInput from "@/components/SmartDateInput";

const TAGS = [
  { key: "fronte", label: "Fronte", icon: User },
  { key: "profilo", label: "Profilo", icon: Person },
  { key: "dietro", label: "Dietro", icon: ArrowUUpLeft },
];

/**
 * Photo gallery grouped by tag. Compare modal shows full images.
 * Props:
 *  - photos: array
 *  - onDelete(id): admin-only — enables trash button
 *  - onEdited(): admin-only — enables edit button; called after save
 */
export default function PhotoGallery({ photos, onDelete, onEdited }) {
  const groups = useMemo(() => {
    const g = { fronte: [], profilo: [], dietro: [], altre: [] };
    for (const p of photos) {
      const k = TAGS.find(t => t.key === p.label)?.key || "altre";
      g[k].push(p);
    }
    // Oldest → newest per tag
    for (const k of Object.keys(g)) g[k].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    return g;
  }, [photos]);

  const [compare, setCompare] = useState({ open: false, tag: null, leftIdx: 0, rightIdx: 0 });
  const [expanded, setExpanded] = useState(false);
  const canExpand = !!onDelete;
  const [editing, setEditing] = useState(null); // photo being edited
  const [editForm, setEditForm] = useState({ date: "", label: "fronte" });
  const [saving, setSaving] = useState(false);

  const openCompare = (tag) => {
    const list = groups[tag] || [];
    if (list.length < 1) return;
    setCompare({ open: true, tag, leftIdx: 0, rightIdx: Math.max(list.length - 1, 0) });
  };
  const compareList = compare.tag ? (groups[compare.tag] || []) : [];
  const step = (side, delta) => {
    setCompare((c) => {
      const cur = side === "left" ? c.leftIdx : c.rightIdx;
      const next = Math.min(Math.max(cur + delta, 0), compareList.length - 1);
      return side === "left" ? { ...c, leftIdx: next } : { ...c, rightIdx: next };
    });
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditForm({ date: p.date || "", label: p.label || "fronte" });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/photos/${editing.photo_id}`, { date: editForm.date, label: editForm.label });
      toast.success("Foto aggiornata");
      setEditing(null);
      onEdited?.();
    } catch (e) {
      toast.error("Errore aggiornamento foto");
    } finally {
      setSaving(false);
    }
  };

  if (photos.length === 0) {
    return <div className="text-sm text-[color:var(--text-secondary)]">Nessuna foto ancora.</div>;
  }

  return (
    <div data-testid="photo-gallery">
      {TAGS.map((t) => {
        const list = groups[t.key];
        if (!list.length) return null;
        return (
          <div key={t.key} className="mb-10">
            <div className="flex items-center justify-between mb-3">
              <div className="label-tiny">{t.label}</div>
              {list.length >= 2 && (
                <Button
                  data-testid={`compare-${t.key}`}
                  size="sm"
                  variant="outline"
                  onClick={() => openCompare(t.key)}
                  className="rounded-full text-xs h-8"
                >
                  <ArrowsLeftRight size={13} weight="duotone" className="mr-1.5" /> Confronta
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {list.map((p) => (
                <div key={p.photo_id} className="relative group overflow-hidden rounded-xl">
                  <img src={p.url} alt={p.label || ""} className="w-full aspect-[3/4] object-cover" />
                  <div
                    className="absolute inset-x-0 bottom-0 p-2 flex items-center justify-between text-xs text-white"
                    style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)" }}
                  >
                    <span className="font-sans tabular-nums">{formatItalianDate(p.date)}</span>
                    <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {onEdited && (
                        <button
                          type="button"
                          data-testid={`edit-photo-${p.photo_id}`}
                          onClick={() => openEdit(p)}
                          className="text-white/90 hover:text-white"
                          title="Modifica foto"
                        >
                          <PencilSimple size={14} weight="bold" />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          data-testid={`delete-photo-${p.photo_id}`}
                          onClick={() => onDelete(p.photo_id)}
                          className="text-white/90 hover:text-white"
                          title="Elimina foto"
                        >
                          <TrashSimple size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {(groups.altre || []).length > 0 && (
        <div className="mb-10">
          <div className="label-tiny mb-3">Altre</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {groups.altre.map((p) => (
              <div key={p.photo_id} className="relative group overflow-hidden rounded-xl">
                <img src={p.url} alt="" className="w-full aspect-[3/4] object-cover" />
                <div className="absolute inset-x-0 bottom-0 p-2 text-xs text-white flex items-center justify-between" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)" }}>
                  <span className="font-sans tabular-nums">{formatItalianDate(p.date)}</span>
                  {onEdited && (
                    <button
                      type="button"
                      data-testid={`edit-photo-${p.photo_id}`}
                      onClick={() => openEdit(p)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-white/90 hover:text-white"
                      title="Modifica foto"
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compare modal — large & responsive, full image visible (object-contain) */}
      <Dialog open={compare.open} onOpenChange={(v) => { setCompare((c) => ({ ...c, open: v })); if (!v) setExpanded(false); }}>
        <DialogContent
          data-testid="compare-modal"
          data-expanded={expanded ? "true" : "false"}
          onEscapeKeyDown={(e) => { if (expanded) { e.preventDefault(); setExpanded(false); } }}
          className={`bg-[color:var(--bg-primary)] max-w-none ${expanded ? "flex flex-col gap-0 overflow-hidden p-0 border-0 rounded-none sm:rounded-none [&>button:last-child]:hidden" : "overflow-y-auto p-4 sm:p-6 w-[min(96vw,1200px)] max-h-[92vh]"}`}
          style={expanded ? { width: "100vw", height: "100vh", maxHeight: "100vh" } : undefined}
        >
          <DialogHeader className={expanded ? "px-3 sm:px-4 pt-3 sm:pt-4" : ""}>
            <div className="flex items-center justify-between gap-3 pr-8">
              <DialogTitle className="font-serif text-2xl">
                Confronto — {TAGS.find(t => t.key === compare.tag)?.label || ""}
              </DialogTitle>
              {canExpand && (expanded ? (
                <Button data-testid="compare-close-fullscreen-btn" size="sm" variant="outline" onClick={() => setExpanded(false)} className="rounded-full">
                  <X size={13} weight="bold" className="mr-1.5" /> Chiudi
                </Button>
              ) : (
                <Button data-testid="compare-expand-btn" size="sm" variant="outline" onClick={() => setExpanded(true)} className="rounded-full">
                  <ArrowsOut size={13} weight="bold" className="mr-1.5" /> Espandi
                </Button>
              ))}
            </div>
          </DialogHeader>

          {/* Tag switcher — jump between Fronte/Profilo/Dietro without closing modal */}
          <div className={`mt-3 flex items-center gap-1.5 flex-wrap ${expanded ? "px-3 sm:px-4" : ""}`} data-testid="compare-tag-switcher">
            {TAGS.map((t) => {
              const list = groups[t.key] || [];
              const disabled = list.length < 1;
              const active = compare.tag === t.key;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`compare-switch-${t.key}`}
                  disabled={disabled}
                  onClick={() => setCompare({
                    open: true,
                    tag: t.key,
                    leftIdx: 0,
                    rightIdx: Math.max(list.length - 1, 0),
                  })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{
                    background: active ? "var(--text-primary)" : "transparent",
                    color: active ? "var(--bg-primary)" : "var(--text-primary)",
                    borderColor: active ? "var(--text-primary)" : "var(--border)",
                  }}
                >
                  <Icon size={12} weight={active ? "fill" : "duotone"} />
                  <span className="uppercase tracking-wider">{t.label}</span>
                  <span className="tabular-nums opacity-70">{list.length}</span>
                </button>
              );
            })}
          </div>

          <div className={`grid ${expanded ? "grid-cols-2 gap-0 flex-1 min-h-0 pb-2" : "grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-2"} mt-3`}>
            {["left", "right"].map((side) => {
              const idx = side === "left" ? compare.leftIdx : compare.rightIdx;
              const p = compareList[idx];
              if (!p) return <div key={side} />;
              return (
                <div key={side} className={`flex flex-col items-center ${expanded ? "min-h-0 h-full min-w-0" : side === "left" ? "sm:items-end" : "sm:items-start"}`}>
                  <div className="text-xs text-[color:var(--text-secondary)] mb-2 uppercase tracking-widest">
                    {side === "left" ? "Precedente" : "Successivo"}
                  </div>
                  <div
                    data-testid={`compare-frame-${side}`}
                    className={`relative flex items-center justify-center ${expanded ? "" : "overflow-hidden rounded-xl"}`}
                    style={expanded ? { flex: 1, height: "100%", overflow: "hidden", minHeight: 0, width: "100%" } : { background: "var(--bg-secondary)", maxHeight: "70vh" }}
                  >
                    <img
                      src={p.url}
                      alt=""
                      data-testid={`compare-image-${side}`}
                      className={expanded ? undefined : "max-w-full w-auto h-auto object-contain"}
                      style={expanded ? { width: "100%", height: "100%", objectFit: "contain", display: "block" } : { maxHeight: "70vh" }}
                    />
                  </div>
                  <div className={`flex items-center gap-3 ${expanded ? "mt-2 flex-shrink-0" : "mt-3"}`}>
                    <button
                      type="button"
                      data-testid={`compare-${side}-prev`}
                      onClick={() => step(side, -1)}
                      disabled={idx === 0}
                      className="w-9 h-9 rounded-full border border-[color:var(--border)] disabled:opacity-30 hover:border-[color:var(--text-primary)] flex items-center justify-center"
                    >
                      <CaretLeft size={14} weight="bold" />
                    </button>
                    <div className="text-center">
                      <div className="font-serif text-lg">{formatItalianDate(p.date)}</div>
                      <div className="text-[10px] text-[color:var(--text-secondary)] tabular-nums">
                        {idx + 1} / {compareList.length}
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid={`compare-${side}-next`}
                      onClick={() => step(side, +1)}
                      disabled={idx >= compareList.length - 1}
                      className="w-9 h-9 rounded-full border border-[color:var(--border)] disabled:opacity-30 hover:border-[color:var(--text-primary)] flex items-center justify-center"
                    >
                      <CaretRight size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit photo modal */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-photo-modal" className="bg-[color:var(--bg-primary)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Modifica foto</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <img src={editing.url} alt="" className="w-full max-h-64 object-contain rounded-xl" style={{ background: "var(--bg-secondary)" }} />
              <div>
                <Label className="label-tiny">Data</Label>
                <SmartDateInput
                  data-testid="edit-photo-date"
                  value={editForm.date}
                  onChange={(iso) => setEditForm((f) => ({ ...f, date: iso }))}
                />
              </div>
              <div>
                <Label className="label-tiny">Inquadratura</Label>
                <div className="mt-1 grid grid-cols-3 gap-2" data-testid="edit-photo-tag-group">
                  {TAGS.map((t) => {
                    const active = editForm.label === t.key;
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        data-testid={`edit-photo-tag-${t.key}`}
                        onClick={() => setEditForm((f) => ({ ...f, label: t.key }))}
                        className="flex flex-col items-center justify-center gap-1 py-2 rounded-xl border transition-all"
                        style={{
                          background: active ? "var(--text-primary)" : "var(--bg-primary)",
                          color: active ? "var(--bg-primary)" : "var(--text-primary)",
                          borderColor: active ? "var(--text-primary)" : "var(--border)",
                        }}
                      >
                        <Icon size={16} weight={active ? "fill" : "duotone"} />
                        <span className="text-[10px] uppercase tracking-wider">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Annulla</Button>
            <Button
              data-testid="save-edit-photo-btn"
              onClick={saveEdit}
              disabled={saving}
              className="rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white"
            >
              Salva modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
